import React from 'react';

type Props = {
  /** Avatar corner: sm ≈ 48px photo, md ≈ 80px+ profile photo */
  variant?: 'avatarOverlay' | 'pill';
  overlay?: 'sm' | 'md';
  /** @deprecated use variant="pill" */
  showLabel?: boolean;
  /** @deprecated use variant="avatarOverlay" + overlay */
  size?: 'sm' | 'md';
  className?: string;
};

const VerifiedIdentityBadge: React.FC<Props> = ({
  variant,
  overlay = 'md',
  showLabel = false,
  size,
  className = '',
}) => {
  let resolved: 'avatarOverlay' | 'pill' = variant ?? (showLabel ? 'pill' : 'avatarOverlay');
  if (!variant && size === 'sm' && !showLabel) {
    resolved = 'avatarOverlay';
  }
  let overlaySize: 'sm' | 'md' = overlay;
  if (!variant && size === 'md' && !showLabel) overlaySize = 'md';
  if (!variant && size === 'sm' && !showLabel) overlaySize = 'sm';

  if (resolved === 'pill') {
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full border border-green-600/45 bg-green-600/10 px-2.5 py-1 text-xs font-semibold text-green-700 ${className}`}
        title="Government ID and selfie verified on BuyDrop"
      >
        <i className="ri-shield-check-fill text-[12px] leading-none" aria-hidden />
        <span>Verified seller</span>
      </span>
    );
  }

  const box = overlaySize === 'sm' ? 'h-[18px] w-[18px]' : 'h-6 w-6';
  const icon = overlaySize === 'sm' ? 'text-base leading-none' : 'text-xl leading-none';

  return (
    <span
      className={`inline-flex ${box} shrink-0 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-black/5 ${className}`}
      title="ID and selfie verified on BuyDrop"
    >
      <i className={`ri-checkbox-circle-fill text-green-600 ${icon}`} aria-hidden />
      <span className="sr-only">Verified seller</span>
    </span>
  );
};

const verifiedMemberPillClass =
  'inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-1 text-xs font-bold leading-snug text-green-800';

export const VerifiedMemberPill: React.FC<{ className?: string }> = ({ className = '' }) => (
  <span
    className={`${verifiedMemberPillClass} shrink-0 ${className}`}
    title="Government ID and selfie verified on BuyDrop"
  >
    <i className="ri-shield-check-fill text-sm leading-none text-green-700" aria-hidden />
    <span>True member</span>
  </span>
);

/** Shield-only — same green pill styling, for avatar corner */
export const VerifiedMemberShieldBadge: React.FC<{ className?: string }> = ({ className = '' }) => (
  <span
    className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-100 ${className}`}
    title="Government ID and selfie verified on BuyDrop"
  >
    <i className="ri-shield-check-fill text-lg leading-none text-green-700" aria-hidden />
    <span className="sr-only">True member</span>
  </span>
);

export const VerifiedMemberSubtext: React.FC<{ className?: string }> = ({ className = '' }) => (
  <p className={`w-full whitespace-nowrap text-center text-[11px] leading-snug text-gray-500 ${className}`}>
    ID + selfie verified
  </p>
);

/** Pill with subtext under the pill only */
export const VerifiedMemberStack: React.FC<{
  className?: string;
  align?: 'start' | 'end';
}> = ({ className = '', align = 'start' }) => (
  <div
    className={`w-fit max-w-full shrink-0 ${
      align === 'end' ? 'self-end' : align === 'start' ? 'self-start' : 'self-center'
    } ${className}`}
  >
    <div className="flex flex-col items-center">
      <VerifiedMemberPill />
      <VerifiedMemberSubtext className="mt-1" />
    </div>
  </div>
);

export default VerifiedIdentityBadge;
